package com.klantenstop.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.auth.AuthGateway
import com.klantenstop.web.auth.AuthModule
import com.klantenstop.web.auth.AuthRole
import com.klantenstop.web.auth.AuthState
import com.klantenstop.web.data.NetworkingConfig
import com.klantenstop.web.data.models.dto.responses.AuthStatusResponse
import com.klantenstop.web.data.models.enums.UserRole
import com.klantenstop.web.data.repos.AuthRepo
import com.klantenstop.web.styles.KsDarkColorScheme
import com.klantenstop.web.styles.KsLightColorScheme
import com.klantenstop.web.styles.LeafletClusterStyles
import com.klantenstop.web.utils.AdManager
import com.klantenstop.web.utils.AnalyticsManager
import com.klantenstop.web.utils.Constants.COLOR_MODE_KEY
import com.klantenstop.web.utils.KlaroConsentManager
import com.klantenstop.web.utils.POSTHOG_CONSENT_KEY
import com.klantenstop.web.utils.SiteGlobals
import com.materialkobweb.MaterialTheme
import com.materialkobweb.components.toast.ToastContainer
import com.materialkobweb.utils.Logger
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.layout.SurfaceVars
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.loadFromLocalStorage
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.vh

@InitSilk
fun initColorMode(ctx: InitSilkContext) {
    MaterialTheme.setSchemes(lightScheme = KsLightColorScheme, darkScheme = KsDarkColorScheme)

    val colorMode = ColorMode.loadFromLocalStorage(COLOR_MODE_KEY) ?: ColorMode.systemPreference

    ctx.config.initialColorMode = colorMode

    // Set document language to Dutch so browsers and search engines know the language.
    document.documentElement?.setAttribute("lang", "nl")

    // Script which runs at load time that needs to be kept in sync with `initialColorMode` above. This code checks
    // if the user's local color mode preference is different from what was exported by Kobweb, replacing it if
    // different to prevent a flash of color after the page loads.
    if (SiteGlobals.isExporting) {
        val node = document.createElement("script").apply {
            textContent = """
                {
                    const storedColor = localStorage.getItem('${COLOR_MODE_KEY}'); // 'LIGHT', 'DARK', or null
                    const desiredColor = storedColor ? `silk-${'$'}{storedColor.toLowerCase()}` : 'silk-dark';
                    const oppositeColor = desiredColor === 'silk-dark' ? 'silk-light' : 'silk-dark';
                    document.documentElement.classList.replace(oppositeColor, desiredColor);
                }
                """.trimIndent()
        }
        document.head?.appendChild(node)
    }

    ctx.stylesheet.apply {
        registerStyleBase("body") {
            Modifier
                .fontFamily(
                    "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                    "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
                )
                .lineHeight(1.4)
                .backgroundColor(SurfaceVars.BackgroundColor.value())
        }
    }
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    LaunchedEffect(SiteGlobals.environment) {
        Logger.init(SiteGlobals.environment)

        // Initialise Klaro consent management (must be before analytics)
        KlaroConsentManager.initialize()

        // Initialise AdSense consent handling (script is loaded only after explicit consent)
        AdManager.initialize(
            clientId = SiteGlobals.adsenseClientId,
            nonPersonalizedOnly = true
        )

        // Initialise PostHog analytics (no-op when key is blank, i.e. local dev)
        AnalyticsManager.initialize(
            apiKey = SiteGlobals.postHogApiKey,
            host = SiteGlobals.postHogHost
        )
    }

    IdentifyUserForAnalytics()
    Style(LeafletClusterStyles)

    AuthModule.init(HttpAuthGateway)

    NetworkingConfig.init(
        baseUrl = SiteGlobals.baseUrl,
        refreshToken = {
            AuthModule.refreshSession()
        },
        onAuthFailure = {
            AuthModule.setUnauthenticated()
            AnalyticsManager.resetSession()
        }
    )

    LaunchedEffect(Unit) { AuthModule.refreshAuthStatus() }

    SilkApp {
        Surface(SmoothColorStyle.toModifier()) {
            Box(
                modifier = Modifier
                    .minHeight(100.vh)
                    .scrollBehavior(ScrollBehavior.Smooth),
                content = { content() }
            )

            // Global toast container
            ToastContainer()
        }
    }
}

private object HttpAuthGateway : AuthGateway {
    override suspend fun login(username: String, password: String) {
        AuthRepo.login(username, password)
    }

    override suspend fun checkAuthStatus(): AuthState {
        return AuthRepo.checkAuthStatus().toAuthState()
    }

    override suspend fun refreshToken() {
        AuthRepo.refreshToken()
    }

    override suspend fun logout() {
        AuthRepo.logout()
    }
}

@Composable
private fun IdentifyUserForAnalytics() {
    // Check authentication status on app initialization
    val authStatus by AuthModule.authStatus.collectAsState()

    LaunchedEffect(Unit) {
        KlaroConsentManager.onConsentChanged(POSTHOG_CONSENT_KEY) { granted ->
            AnalyticsManager.setConsent(granted)
            if (granted) {
                val status = AuthModule.authStatus.value
                if (status?.authenticated == true) {
                    identifyUserToPostHog(status)
                }
            }
        }
    }

    // When auth status changes, identify the user if consent is already granted.
    LaunchedEffect(authStatus) {
        val status = authStatus ?: return@LaunchedEffect
        if (!status.authenticated) return@LaunchedEffect

        // Only identify if consent has been explicitly granted
        val hasConsent = KlaroConsentManager.getConsent(POSTHOG_CONSENT_KEY)
        if (hasConsent) {
            identifyUserToPostHog(status)
        }
    }
}

private fun identifyUserToPostHog(status: AuthState) {
    val role = when (status.role) {
        AuthRole.Admin -> "admin"
        AuthRole.Moderator -> "moderator"
        else -> "consumer"
    }
    val userId = status.userId

    if (!userId.isNullOrBlank()) {
        Logger.debug("Analytics", "Identifying user: $userId")
        AnalyticsManager.identifyUser(
            distinctId = userId,
            role = role
        )
    }
}

private fun AuthStatusResponse.toAuthState() = AuthState(
    authenticated = authenticated,
    role = when (roleType) {
        UserRole.Admin -> AuthRole.Admin
        UserRole.Moderator -> AuthRole.Moderator
        else -> AuthRole.Consumer
    },
    userId = userId
)
